import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const PRICE_TYPE_LABEL = {
  per_unit_per_day: "/each/day",
  per_unit_flat: "/each",
  flat_per_day: "/day",
  flat: " flat",
};

function computeExtraCost(extra, quantity, days) {
  switch (extra.price_type) {
    case "per_unit_per_day":
      return extra.price * quantity * days;
    case "per_unit_flat":
      return extra.price * quantity;
    case "flat_per_day":
      return extra.price * days;
    default:
      return extra.price;
  }
}

export default function ItemDetailDialog({ item, open, onOpenChange }) {
  const cart = useCart();
  const [qty, setQty] = useState(1);
  const [selectedExtras, setSelectedExtras] = useState({});
  const [note, setNote] = useState("");

  // Reset state every time a new item is loaded into the dialog so that
  // default-true extras (e.g. tent-weights) get pre-checked correctly.
  useEffect(() => {
    if (!item) return;
    setQty(1);
    setNote("");
    setSelectedExtras(
      Object.fromEntries(
        (item.extras || []).filter((e) => e.default).map((e) => [e.id, true])
      )
    );
  }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!item) return null;

  const days = cart.rentalDays;
  const lineBase = item.price_per_day * qty * days;
  const extrasArr = (item.extras || []).filter((e) => selectedExtras[e.id]);
  const extrasCost = extrasArr.reduce(
    (s, e) => s + computeExtraCost(e, qty, days),
    0
  );
  const totalPreTax = lineBase + extrasCost;

  const overflow = item.available !== undefined && qty > item.available;

  const goingFast = item.going_fast && !item.sold_out;

  const handleAdd = () => {
    if (item.sold_out) {
      toast.error(`${item.name} is fully booked on this date — try a different day.`);
      return;
    }
    if (overflow) {
      toast.error(
        `Only ${item.available} ${item.name}${item.available === 1 ? "" : "s"} available on ${new Date(
          cart.startDate + "T00:00:00"
        ).toLocaleDateString("en-US", { month: "short", day: "numeric" })}.`
      );
      return;
    }
    const extras = Object.entries(selectedExtras)
      .filter(([, v]) => v)
      .map(([k]) => k);
    cart.add(item, qty, extras, note.trim());
    cart.setOpen(true);
    onOpenChange(false);
    toast.success(`${qty} × ${item.name} added to your pack`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-white rounded-[32px] p-0 overflow-hidden border border-slate-100 shadow-2xl"
        data-testid="item-detail-dialog"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{item.name}</DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2">
          {/* Image */}
          <div className="relative aspect-square md:aspect-auto bg-slate-100 overflow-hidden">
            {item.image_url ? (
              <img
                src={item.image_url}
                alt={item.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-brand-blue">
                <i className={`fas ${item.icon} text-7xl`} />
              </div>
            )}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              {item.instant_booking && (
                <span className="bg-brand-gold text-brand-blue text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full inline-flex items-center gap-2">
                  <i className="fas fa-bolt" /> Instant Booking
                </span>
              )}
              {goingFast && (
                <span className="bg-brand-red text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full inline-flex items-center gap-2">
                  <i className="fas fa-fire" /> Going Fast
                </span>
              )}
              {item.sold_out && (
                <span className="bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full inline-flex items-center gap-2">
                  Fully Booked This Date
                </span>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="p-6 md:p-8 flex flex-col">
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-2">
              Park Hill Pod
            </p>
            <h2
              className="text-2xl md:text-3xl font-black text-brand-blue uppercase tracking-tightest leading-tight"
              data-testid="item-detail-name"
            >
              {item.name}
            </h2>
            <p className="text-sm text-slate-600 font-medium mt-2 leading-relaxed">
              {item.description}
            </p>

            <div className="mt-5 flex items-baseline gap-2">
              <span className="text-3xl font-black text-brand-red">
                ${item.price_per_day.toFixed(2)}
              </span>
              <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                / day
              </span>
            </div>

            {/* Quantity */}
            <div className="mt-6">
              <label className="text-[10px] font-black uppercase tracking-widest text-brand-blue">
                Quantity
              </label>
              <div className="mt-2 flex items-center gap-3">
                <div className="flex items-center bg-slate-100 rounded-xl p-1">
                  <button
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    className="w-9 h-9 rounded-lg hover:bg-white text-brand-blue active:scale-95 transition-all"
                    data-testid="item-detail-decrement"
                  >
                    <i className="fas fa-minus" />
                  </button>
                  <input
                    type="number"
                    min={1}
                    value={qty}
                    onChange={(e) =>
                      setQty(Math.max(1, parseInt(e.target.value) || 1))
                    }
                    className="w-14 text-center font-black text-brand-blue text-lg bg-transparent outline-none"
                    data-testid="item-detail-qty"
                  />
                  <button
                    onClick={() => setQty(qty + 1)}
                    className="w-9 h-9 rounded-lg bg-brand-blue text-white shadow-md active:scale-95 transition-all"
                    data-testid="item-detail-increment"
                  >
                    <i className="fas fa-plus" />
                  </button>
                </div>
                {overflow && (
                  <p
                    className="text-xs font-black uppercase tracking-widest text-brand-red"
                    data-testid="item-detail-availability-warning"
                  >
                    Only {item.available} available on this date
                  </p>
                )}
              </div>
            </div>

            {/* Extras */}
            {item.extras && item.extras.length > 0 && (
              <div className="mt-6">
                <label className="text-[10px] font-black uppercase tracking-widest text-brand-blue">
                  Add-ons & Service
                </label>
                <div className="mt-2 space-y-2 max-h-56 overflow-y-auto pr-1 cart-scroll">
                  {item.extras.map((e) => {
                    const checked = !!selectedExtras[e.id];
                    const cost = computeExtraCost(e, qty, days);
                    return (
                      <label
                        key={e.id}
                        className={`flex items-start gap-3 rounded-2xl border p-3 cursor-pointer transition-colors ${
                          checked
                            ? "border-brand-blue bg-brand-blue/5"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`item-detail-extra-${e.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(ev) =>
                            setSelectedExtras((prev) => ({
                              ...prev,
                              [e.id]: ev.target.checked,
                            }))
                          }
                          className="mt-1 accent-brand-blue w-4 h-4"
                          data-testid={`item-detail-extra-${e.id}-checkbox`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-black uppercase text-brand-blue tracking-tightest">
                            {e.label}
                          </p>
                          {e.description && (
                            <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                              {e.description}
                            </p>
                          )}
                          <p className="text-[10px] font-black uppercase tracking-widest text-brand-red mt-1">
                            +${e.price.toFixed(2)}
                            {PRICE_TYPE_LABEL[e.price_type] || ""}
                          </p>
                        </div>
                        {checked && (
                          <span className="text-sm font-black text-brand-blue whitespace-nowrap">
                            +${cost.toFixed(2)}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Note */}
            <div className="mt-6">
              <label
                htmlFor={`note-${item.id}`}
                className="text-[10px] font-black uppercase tracking-widest text-brand-blue"
              >
                Note for the Pod team (optional)
              </label>
              <textarea
                id={`note-${item.id}`}
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 240))}
                rows={2}
                placeholder="e.g. Drop-off at side gate, or specific color preference"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 resize-none"
                data-testid="item-detail-note-input"
              />
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mt-1">
                {note.length}/240
              </p>
            </div>

            {/* Footer / CTA */}
            <div className="mt-6 pt-5 border-t border-slate-100">
              <div className="flex items-baseline justify-between mb-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {qty} × {days} day{days > 1 ? "s" : ""}
                </p>
                <p
                  className="text-2xl font-black text-brand-blue"
                  data-testid="item-detail-line-total"
                >
                  ${totalPreTax.toFixed(2)}
                </p>
              </div>
              <button
                onClick={handleAdd}
                disabled={item.sold_out || overflow}
                className="btn-primary w-full py-4 rounded-2xl font-black uppercase tracking-widest text-sm flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="item-detail-add-button"
              >
                <i className="fas fa-plus" />
                {item.sold_out ? "Fully Booked" : `Reserve & Add to Pack`}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
